import isEqual from 'lodash-es/isEqual';
import { isValidElement } from 'react';
import { DataListItem } from '@brighte/form-components';
import {
  AddressType,
  ApplicationInputType,
  EnterpriseStructure,
  OwnershipDetailsType,
  OwnershipType,
} from '@brighte/validation';
import { getEnumKey } from '@components/StepNavigator/helper';
import { useGetSteps } from '@pages/ApplicationForm/hooks';
import { useApplicationData, useStepperActions, useStepperIndex } from '@provider';
import { APPLICATION_STEPS, formatAddress, pascalToCamelCase } from '../../utils';

const formatName = (person: { firstName: string; lastName: string }) => `${person.firstName} ${person.lastName}`;

const formatDirectorAddress = (address: AddressType | null | undefined): string => {
  if (!address) return '';
  if ('fullAddress' in address && (address as Record<string, unknown>).fullAddress) {
    return String((address as Record<string, unknown>).fullAddress);
  }
  return formatAddress(address);
};

export const useOwnershipDetailsSummary = (): { title: string; items: DataListItem[] }[] => {
  const ownershipDetails = useApplicationData('OwnershipDetails');

  if (!ownershipDetails) return [];

  const { structure } = ownershipDetails.enterprise;

  if (structure === EnterpriseStructure.Company) {
    const { company } = ownershipDetails.enterprise;
    const sections: { title: string; items: DataListItem[] }[] = [
      { title: '', items: [{ label: 'Ownership type', value: company.ownershipType }] },
    ];

    if (company.ownershipType === OwnershipType.Proprietary) {
      sections.push(
        {
          title: 'Directors',
          items: company.directors.map((d, i) => ({
            label: `Director ${i + 1}`,
            value: formatName(d),
          })),
        },
        {
          title: 'Beneficial owners',
          items: company.beneficialOwners.map((bo, i) => ({
            label: `Beneficial owner ${i + 1}`,
            value: formatName(bo),
          })),
        },
      );
    }

    return sections;
  }

  if (structure === EnterpriseStructure.UnincorporatedAssociation) {
    const { unincorporatedAssociation: ua } = ownershipDetails.enterprise;
    return [
      {
        title: '',
        items: [
          ...ua.chairman.map(c => ({ label: 'Chairman', value: formatName(c) })),
          ...ua.secretary.map(s => ({ label: 'Secretary', value: formatName(s) })),
          ...ua.treasurer.map(t => ({ label: 'Treasurer', value: formatName(t) })),
        ],
      },
    ];
  }

  if (structure === EnterpriseStructure.IncorporatedAssociation) {
    const { incorporatedAssociation: ia } = ownershipDetails.enterprise;
    return [
      {
        title: '',
        items: [
          ...ia.chairman.map(c => ({ label: 'Chairman', value: formatName(c) })),
          ...ia.secretary.map(s => ({ label: 'Secretary', value: formatName(s) })),
          ...ia.treasurer.map(t => ({ label: 'Treasurer', value: formatName(t) })),
        ],
      },
    ];
  }

  if (structure === EnterpriseStructure.Partnership) {
    const { partnership } = ownershipDetails.enterprise;
    return (partnership?.directors ?? []).map((d, i) => ({
      title: `Director #${i + 1}`,
      items: [
        { label: 'Name', value: formatName(d) },
        { label: 'Home address', value: formatDirectorAddress(d.address) },
      ],
    }));
  }

  if (structure === EnterpriseStructure.ProprietaryLimitedCompany) {
    const { proprietaryLimitedCompany: plc } = ownershipDetails.enterprise;
    return (plc?.directors ?? []).map((d, i) => ({
      title: `Director #${i + 1}`,
      items: [
        { label: 'Name', value: formatName(d) },
        { label: 'Home address', value: formatDirectorAddress(d.address) },
      ],
    }));
  }

  if (structure === EnterpriseStructure.Trust) {
    const { trust } = ownershipDetails.enterprise;
    return (trust?.directors ?? []).map((d, i) => ({
      title: `Director #${i + 1}`,
      items: [
        { label: 'Name', value: formatName(d) },
        { label: 'Home address', value: formatDirectorAddress(d.address) },
      ],
    }));
  }

  if (structure === EnterpriseStructure.SoleTrader) {
    const individual = ownershipDetails.enterprise.soleTrader?.individual;
    if (!individual) return [];
    return [
      {
        title: '',
        items: [
          { label: 'Name', value: formatName(individual) },
          { label: 'Home address', value: formatDirectorAddress(individual.address) },
        ],
      },
    ];
  }

  if (structure === EnterpriseStructure.Other) {
    const individual = ownershipDetails.enterprise.other?.individual;
    if (!individual) return [];
    return [
      {
        title: '',
        items: [
          { label: 'Name', value: formatName(individual) },
          { label: 'Home address', value: formatDirectorAddress(individual.address) },
        ],
      },
    ];
  }

  return [];
};

export const useBeforeNextStep = () => {
  const steps = useGetSteps();
  const { onNext } = useStepperActions();
  const { previousStepIndex } = useStepperIndex();

  const { BusinessDetails: businessDetails, OwnershipDetails: ownershipDetails } = useApplicationData([
    'BusinessDetails',
    'OwnershipDetails',
  ]);

  const structure = businessDetails?.enterprise?.structure;
  const structureProp = structure ? pascalToCamelCase(getEnumKey(structure, EnterpriseStructure)) : undefined;

  const guarantorDetailIndex = steps.findIndex(
    step => isValidElement(step) && step.key === APPLICATION_STEPS.GUARANTOR_DETAILS,
  );
  const summaryIndex = steps.findIndex(step => isValidElement(step) && step.key === APPLICATION_STEPS.SUMMARY);
  const repaymentAccountIndex = steps.findIndex(
    step => isValidElement(step) && step.key === APPLICATION_STEPS.REPAYMENT_ACCOUNT,
  );

  const handleBeforeNextStep = (formValues: ApplicationInputType) => {
    // If directors have changed and we're coming from a previous step (editing), navigate to Guarantor
    const previousDirectors = structureProp ? ownershipDetails?.enterprise?.[structureProp]?.directors : undefined;
    const latestDirectors = structureProp
      ? (formValues as OwnershipDetailsType)?.enterprise?.[structureProp]?.directors
      : undefined;

    const directorsChanged = !isEqual(previousDirectors, latestDirectors);
    const isComingFromSummaryOrRepayment =
      (summaryIndex !== -1 && previousStepIndex === summaryIndex) ||
      (repaymentAccountIndex !== -1 && previousStepIndex === repaymentAccountIndex);
    const shouldNavigate = isComingFromSummaryOrRepayment && directorsChanged && guarantorDetailIndex !== -1;

    if (shouldNavigate) {
      onNext(guarantorDetailIndex);
      return true;
    }

    return false;
  };

  return { handleBeforeNextStep };
};
